import { applyTheme } from './theme';

const ACCESS_TOKEN_KEY = 'helpdesk_customer_token';
const REFRESH_TOKEN_KEY = 'helpdesk_customer_refresh_token';
const USER_KEY = 'helpdesk_customer_user';
const EMBED_REQUESTER_KEY = 'helpdesk_customer_embed_requester';
export const AUTH_CHANGED_EVENT = 'helpdesk:customer-auth-changed';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface StorageOptions {
  emit?: boolean;
}

export interface CustomerUserInfo {
  id: string;
  email: string;
  fullName: string;
  organizationId: string;
  projectIds: string[];
}

export interface EmbedRequesterContext {
  requesterName: string;
  requesterEmail?: string;
  requesterPhone?: string;
  externalUserId?: string;
}

function emitAuthChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  }
}

function shouldEmit(options?: StorageOptions): boolean {
  return options?.emit !== false;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getUser(): CustomerUserInfo | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setAuth(accessToken: string, refreshToken: string, user: CustomerUserInfo, options?: StorageOptions): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (shouldEmit(options)) {
    emitAuthChanged();
  }
}

export function updateTokens(accessToken: string, refreshToken: string, options?: StorageOptions): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (shouldEmit(options)) {
    emitAuthChanged();
  }
}

export function setUser(user: CustomerUserInfo, options?: StorageOptions): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (shouldEmit(options)) {
    emitAuthChanged();
  }
}

export function getEmbedRequesterContext(): EmbedRequesterContext | null {
  const raw = localStorage.getItem(EMBED_REQUESTER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as EmbedRequesterContext;
  } catch {
    return null;
  }
}

export function setEmbedRequesterContext(context: EmbedRequesterContext | null): void {
  if (!context) {
    localStorage.removeItem(EMBED_REQUESTER_KEY);
    return;
  }

  localStorage.setItem(EMBED_REQUESTER_KEY, JSON.stringify(context));
}

export function clearAuth(options?: StorageOptions): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EMBED_REQUESTER_KEY);
  if (shouldEmit(options)) {
    emitAuthChanged();
  }
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

interface ProfileResult {
  kind: 'success' | 'unauthorized' | 'error';
  user?: CustomerUserInfo;
}

async function fetchProfile(accessToken: string): Promise<ProfileResult> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/customer/profile`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (res.status === 401) {
      return { kind: 'unauthorized' };
    }

    if (!res.ok) {
      return { kind: 'error' };
    }

    const body = await res.json();
    if (!body.success || !body.data) {
      return { kind: 'error' };
    }

    const portalTheme = body.data.organization?.portalTheme;
    if (portalTheme === 'sky' || portalTheme === 'warm' || portalTheme === 'dark') {
      applyTheme(portalTheme);
    }

    return {
      kind: 'success',
      user: {
        id: body.data.id,
        email: body.data.email,
        fullName: body.data.fullName,
        organizationId: body.data.organization?.id ?? '',
        projectIds: Array.isArray(body.data.projects) ? body.data.projects.map((project: { id: string }) => project.id) : [],
      },
    };
  } catch {
    return { kind: 'error' };
  }
}

async function refreshSession(refreshToken: string): Promise<'success' | 'unauthorized' | 'error'> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/customer/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (res.status === 401) {
      return 'unauthorized';
    }

    if (!res.ok) {
      return 'error';
    }

    const body = await res.json();
    if (!body.success || !body.data) {
      return 'error';
    }

    updateTokens(body.data.accessToken, body.data.refreshToken, { emit: false });
    return 'success';
  } catch {
    return 'error';
  }
}

function hasUsableStoredUser(user: CustomerUserInfo | null): user is CustomerUserInfo {
  return !!user?.id && !!user.email;
}

export async function bootstrapSession(): Promise<CustomerUserInfo | null> {
  const storedUser = getUser();
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!accessToken && !refreshToken) {
    clearAuth({ emit: false });
    return null;
  }

  if (accessToken) {
    const profile = await fetchProfile(accessToken);
    if (profile.kind === 'success' && profile.user) {
      setUser(profile.user, { emit: false });
      return profile.user;
    }

    if (profile.kind === 'error' && hasUsableStoredUser(storedUser)) {
      return storedUser;
    }
  }

  if (refreshToken) {
    const refreshResult = await refreshSession(refreshToken);
    if (refreshResult === 'success') {
      const nextAccessToken = getAccessToken();
      if (nextAccessToken) {
        const profile = await fetchProfile(nextAccessToken);
        if (profile.kind === 'success' && profile.user) {
          setUser(profile.user, { emit: false });
          return profile.user;
        }

        if (profile.kind === 'error' && hasUsableStoredUser(storedUser)) {
          return storedUser;
        }
      }
    }

    if (refreshResult === 'error' && hasUsableStoredUser(storedUser)) {
      return storedUser;
    }
  }

  clearAuth({ emit: false });
  return null;
}
